<script setup lang="ts">
import { RouterLink } from "vue-router";
import type { BoxLayout, DisplayMode } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();

import {
  buildUrlPrefix,
  getProductName,
  getProductRoute,
  getProductFromPrice,
  getBiggestThumbnailUrl,
} from "@shopware/helpers";
import { toRefs, computed, ref } from "vue";
import { defu } from "defu";
import SwListingProductPrice from "././../product/SwListingProductPrice.vue";
import {
  useAddToCart,
  useNotifications,
  useProductWishlist,
  useUrlResolver,
} from "#imports";
import { useElementSize } from "@vueuse/core";
import type { Schemas } from "#shopware";
import { ApiClientError } from "@shopware/api-client";
const { t } = useI18n();
const localePath = useLocalePath();

const { formatLink } = useInternationalization(localePath);

const { pushSuccess, pushError, pushWarning } = useNotifications();

const props = withDefaults(
  defineProps<{
    product: Schemas["Product"];
    layoutType?: BoxLayout;
    isProductListing?: boolean;
    displayMode?: DisplayMode;
  }>(),
  {
    layoutType: "standard",
    displayMode: "standard",
    isProductListing: false,
  }
);

type Translations = {
  product: {
    addedToWishlist: string;
    removedFromTheWishlist: string;
    reason: string;
    cannotAddToWishlist: string;
    addedToCart: string;
    addToCart: string;
    details: string;
    badges: {
      topseller: string;
    };
  };
};

let translations: Translations = {
  product: {
    addedToWishlist: "has been added to wishlist.",
    removedFromTheWishlist: "has been removed from wishlist.",
    reason: "Reason",
    cannotAddToWishlist: "cannot be added to wishlist.",
    addedToCart: "has been added to cart.",
    addToCart: "Add to cart",
    details: "Details",
    badges: {
      topseller: "Tip",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product } = toRefs(props);

// const { addToCart, isInCart, count } = useAddToCart(product);

const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(
  product.value.id
);
const isLoading = ref(false);

const toggleWishlistProduct = async () => {
  if (!isInWishlist.value) {
    try {
      await addToWishlist();
      return pushSuccess(
        `<b>${props.product?.translated?.name}</b>` +
          t("product.messages.addedToWishlist")
      );
    } catch (error) {
      if (error instanceof ApiClientError) {
        const reason = error.details.errors?.[0]?.detail
          ? `${translations.product.reason}: ${error.details.errors?.[0]?.detail}`
          : "";
        return pushError(
          `<b>${props.product?.translated?.name}</b>` +
            t("product.messages.annotAddToWishlist") +
            `\n${reason}`
        );
      }
    }
  }
  pushWarning(
    `<b>${props.product?.translated?.name}</b> ` +
      t("product.messages.removedFromWishlist")
  );
  removeFromWishlist();
};

const { getUrlPrefix } = useUrlResolver();

const imageElement = ref(null);
const { height } = useElementSize(imageElement);

const DEFAULT_THUMBNAIL_SIZE = 10;
function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed(() => {
  return encodeURI(`${getBiggestThumbnailUrl(
    product.value?.cover?.media
  )}?&height=${roundUp(height.value)}&fit=cover`);
});

const isSale = computed(() => {
  return (
    product.value &&
    product.value.calculatedPrice &&
    product.value.calculatedPrice.listPrice &&
    product.value.calculatedPrice.listPrice.discount < 0
  );
});

const isBestPrice = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("019474d44f8976dcb4d544a70df761da")
  );
});

const isHit = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("01929aabd1c7794394a95b6e0c5891fe")
  );
});

const isLimitedEdition = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("01929aac67377f62a7c5837237ba4ba7")
  );
});

const isSodaBrand = computed(() => {
  return (
    product.value.tagIds &&
    product.value.tagIds.includes("0197f8c24449749a9bb7ea9ff4d482e4")
  );
});

const fromPrice = getProductFromPrice(props.product);

const addToCartVisible = ref(false);
const selectedVariant = ref<Schemas["Product"] | null>(null);
const updateSelectedVariant = (variant: any) => {
  selectedVariant.value = variant;
};
const isAddToCartProcessing = ref(false);
const miniCartModal = useMiniCartModal();
const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  const { addToCart } = useAddToCart( 
    product?.value?.childCount > 0 || product?.value?.parentId
      ? selectedVariant.value
      : product
  );
  await addToCart();

  const errors = getErrorsCodes();

  errors?.forEach((element) => {
    const { messageKey, params } = resolveCartError(element);
    pushError(t(`errors.${messageKey}`, params as Record<string, unknown>));
    isAddToCartProcessing.value = false;
  });

  if (!errors.length)
    pushSuccess(
      `${props.product?.translated.name} ${translations.product.addedToCart}`
    );

  isAddToCartProcessing.value = false;
  addToCartVisible.value = false;
  miniCartModal.open();
};
</script>

<template>
  <div
    class="cards__item pe-[0px] ps-[0px] pb-7 md:pb-10 w-full nach"
    data-testid="product-box"
  >
    <div class="inline-block h-full w-full product_item_wrap">
      <div class="w-full mb-3 relative">
        <figure
          class="d-block w-full h-[300x] sm:h-[270px] md:h-[400px] lg:h-[475px] m-0 relative"
        >
          <!-- <a href="#" class="absolute start-0 top-0 w-full h-full"></a> -->
          <RouterLink
            :to="formatLink(getProductRoute(product))"
            class="overflow-hidden"
          >
            <NuxtImg
              ref="imageElement"
              loading="lazy"
              format="webp"
              :src="srcPath"
              :alt="getProductName({ product }) || ''"
              class="img-cover mCS_img_loaded"
              width="308"
              height="430"
              data-testid="product-box-img"
            />
          </RouterLink>

          <span
            :disabled="isLoading"
            @click="toggleWishlistProduct"
            class="fav-icon top-[10px] end-[10px] absolute text-black03 text-xl"
          >
            <i v-if="isInWishlist" class="fa-solid fa-heart"></i>
            <i v-else class="fa-regular fa-heart"></i>
          </span>
          <div class="absolute bottom-0 lg:top-3 left-0 lg:left-2">
            <div
              v-if="isSodaBrand"
              class="font-bold text-black text-center rounded-sm text-sm px-5 uppercase mb-1"
              style="background-color: #CCFF00;"
            >
              {{ $t("product.badges.soda") }}
            </div>
            <div
              v-if="isHit"
              class="font-bold bg-pink text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.hit") }}
            </div>
            <div
                v-else-if="isBestPrice"
                class="font-bold bg-black text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.bestprice") }}
            </div>
            <div
              v-else-if="isSale"
              class="font-bold bg-red-600 text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.sale") }}
            </div>
            
            <div
              v-else-if="product.isNew"
              class="font-bold bg-white text-black text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.new") }}
            </div>
            <div
              v-else-if="isLimitedEdition"
              class="font-bold bg-black03 text-white text-center rounded-sm text-sm px-5"
            >
              {{ $t("badge.limited") }}
            </div>
            
          </div>
        </figure>

        <button
          type="button"
          class="absolute z-10 right-2 bottom-2 md:flex btn-produ-slide items-center justify-center btn-outline text-nowrap p-0 h-[34px] w-[34px] md:h-[44px] md:w-[44px] text-lg group"
          data-testid="add-to-cart-button"
          @click="addToCartVisible = !addToCartVisible"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="667" viewBox="0 0 845 667" fill="none" class="group-hover:[&>path]:fill-white mt-[3px] w-[20px] md:w-[25px]">
            <path d="M337 564.4C365.3 564.4 388.2 587.3 388.1 615.6C388.1 643.9 365.2 666.8 337 666.8C308.7 666.8 285.8 643.9 285.8 615.6C285.8 587.3 308.7 564.4 337 564.4ZM664.7 564.4C693 564.4 715.9 587.3 715.9 615.6C715.9 643.9 693 666.8 664.7 666.8C636.4 666.8 613.5 643.9 613.6 615.6C613.6 587.3 636.5 564.4 664.7 564.4ZM123.7 1.20001C141.8 1.20001 160.4 4.7 180.6 16.8C199.9 28.4 215.3 46.1 225 69.6L228.4 78.6L229.8 84.2L248.3 197.3L279.7 396.6C283.1 417.5 300.1 433.3 320.7 435.6L326 435.9H673.2C694.5 435.9 712.8 421.6 718.5 400.8L719.7 395.4L768.3 134.9C772.2 114.1 792.2 100.3 813.1 104.2C832.3 107.8 845.5 125.2 844.4 144.2L843.8 149L795.3 408.5C786.1 466 738.2 508.9 680.8 512.4L673.2 512.7H326C265.4 512.7 213.7 468.8 203.9 408.7L167.5 178.4L154.7 100.8L153.7 98C151.1 92 147.8 87.7 143.9 84.7L140.9 82.6C136.5 80 132.1 78.7 127.9 78.2L123.7 78H38.4C17.2 78 0 60.8 0 39.6C0 20 14.6 3.9 33.6 1.5L38.4 1.20001H123.7ZM505.9 0C525.5 0 541.6 14.6 544 33.6L544.3 38.4V110.9H616.9C638.1 110.9 655.3 128.1 655.3 149.3C655.3 168.9 640.7 185 621.7 187.4L616.9 187.7H544.3V260.2C544.3 281.4 527.1 298.6 505.9 298.6C486.3 298.6 470.2 284 467.8 265.1L467.5 260.2V187.7H395C373.8 187.7 356.6 170.5 356.6 149.3C356.6 129.7 371.2 113.6 390.2 111.2L395 110.9H467.5V38.4C467.5 17.2 484.7 0 505.9 0Z" fill="black"/>
          </svg>
        </button>

        <RouterLink
          :to="formatLink(getProductRoute(product))"
          class="absolute start-0 top-0 w-full h-full flex items-center justify-center overlay-hover uppercase text-white hidden md:flex"
          style="background-color: rgba(0, 0, 0, 0.3)"
        >
          {{ $t("button.buynow.listing") }}
          <i class="fa-solid fa-angle-right"></i>
        </RouterLink>
      </div>
      <div class="pt-1">
        <p class="text-sm md:text-lg pe-[15px] ps-0 md:ps-[15px]">
          <RouterLink
            :to="formatLink(getProductRoute(product))"
            class="hover:text-pink truncate"
          >
            {{ getProductName({ product }) }}
          </RouterLink>
        </p>
        <p class="uppercase flex bg-white z-10 pe-[15px] ps-0 md:ps-[15px]">
          <SwListingProductPrice
            :product="product"
            class="flex gap-2 flex-row-reverse"
            data-testid="product-box-product-price"
          />
        </p>
      </div>

      <Transition name="slide-up">
        <div
          v-if="addToCartVisible"
          class="fixed inset-0 w-full h-full z-[45] bg-[#09090961]"
          @click="addToCartVisible = false"
        >
          <div
            class="fixed bottom-0 left-0 right-0 bg-white min-h-[100px] border-t border-gray-200 p-4 z-50 sticky-add-to-cart w-full"
            @click.stop
          >
            <div
              class="container px-0 md:px-4 mx-auto flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-0 md:gap-4"
            >

              <!-- Loader -->
              <div v-if="!selectedVariant" class="flex justify-center items-center min-h-[100px]">
                <i class="fa-solid fa-spinner animate-spin text-2xl"></i>
              </div>

              <!-- Variant Configurator -->
              <div class="w-full md:w-auto"
                v-show="selectedVariant"
              >
                <ProductDetailListingVariantConfigurator
                  @change="updateSelectedVariant"
                  :productId="
                    product.childCount
                      ? product.id
                      : (product.parentId ?? product.id)
                  "
                  :isMain="product.parentId ? false : true"
                />
              </div>

              <div
                class="w-full md:w-auto flex justify-end md:justify-start mt-3"
              >
                <button
                  type="button"
                  class="btn_secondary w-full md:w-auto md:min-w-[310px]"
                  :class="{
                    'opacity-50 cursor-not-allowed':
                      !selectedVariant || isAddToCartProcessing,
                  }"
                  data-testid="add-to-cart-button"
                  :disabled="!selectedVariant || isAddToCartProcessing"
                  @click="addToCartProxy"
                >
                  {{ $t("product.add_to_basket") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>
